import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useMedia } from 'react-use';
import { DividerWithLogo, Loading, LazyLoadImage, LazyLoadSource } from 'ck-ui';
import classnames from 'classnames';
import { getImgixUrl } from 'ck-utils';
import { Configure, InstantSearch } from 'react-instantsearch/dom';
import { connectInfiniteHits, connectStateResults } from 'react-instantsearch-dom';
import { trackProductImpressions } from 'ck-utils/algoliaAnalytics';
import { algoliaClient, INDEX_PRODUCTS, ALGOLIA_BASE_FILTERS } from 'src/lib/algolia';
import { useWishlist } from 'ck-utils/hooks/useWishlist';
import { ProductHit } from '../ProductHit';
import styles from './CollectionList.module.scss';

const CollectionListContainer = ({ enableCarousel, collection, showShopAll }) => {
  const { handle, title, shortDescription, desktopImage, mobileImage, buttonText, boostUrl, hitsPerPage } = collection;
  const isMobile = useMedia('(max-width: 767px)');
  // eslint-disable-next-line no-nested-ternary
  const defaultIndex = INDEX_PRODUCTS;
  const filters = useMemo(
    () => `${ALGOLIA_BASE_FILTERS} AND collectionHandles:${handle}`,
    [handle]);
  const optionalFilters = useMemo(
    () => (boostUrl ? boostUrl.split('boost=')[1].split(';') : null),
    [boostUrl]);

  return (
    <InstantSearch
      indexName={defaultIndex}
      searchClient={algoliaClient}
    >
      <Configure
        optionalFilters={optionalFilters}
        hitsPerPage={hitsPerPage}
        filters={filters}
        clickAnalytics
        ruleContexts={optionalFilters ? undefined : [`collection-${handle}`]}
        enableRules={!optionalFilters}
        analyticsTags={['web', 'collection', 'collection-list', handle]}
      />
      <>
        {desktopImage && mobileImage && (
          <div className="columns is-mobile collection-banner">
            <div className="column is-12">
              <a title={title} href={`/collections/${handle}`} className="image-wrapper">
                <picture>
                  <LazyLoadSource media="(max-width: 1087px)" srcSet={getImgixUrl(mobileImage, ['w=800'])} />
                  <LazyLoadSource media="(min-width: 1088px)" srcSet={getImgixUrl(desktopImage, ['w=1920'])} />
                  <LazyLoadImage src={getImgixUrl(desktopImage, ['w=1920'])} alt={title} />
                </picture>
              </a>
            </div>
          </div>
        )}

        {shortDescription && <div className="section-header__description">{shortDescription}</div>}

        <ProductHitList enableCarousel={enableCarousel} isMobile={isMobile} listName={title} />

        { showShopAll && (
          <>
            <div className={classnames('btn-shop-container', { 'has-text-centered': isMobile })}>
              <a title={title} href={`/collections/${handle}`} className="button secondary fullwidth-mobile">
                {buttonText || `Shop all ${title}`}
              </a>
            </div>
            <DividerWithLogo />
          </>
        )}
      </>
    </InstantSearch>
  );
};

CollectionListContainer.propTypes = {
  collection: PropTypes.object.isRequired,
  enableCarousel: PropTypes.bool.isRequired,
  showShopAll: PropTypes.bool
};

CollectionListContainer.defaultProps = {
  showShopAll: true
};

const ProductHitList = connectInfiniteHits(connectStateResults(({
  listName,
  isMobile,
  isSearchStalled,
  searchResults,
  hits,
  enableCarousel
}) => {
  const { nbHits, queryID, hitsPerPage, page, hits: sHits, index: indexName } = searchResults || {};
  const sliderRef = useRef(null);
  const { wishListProductIds, wishListCollections } = useWishlist();
  const isCarousel = !isMobile || enableCarousel;

  // Fire product impression tracking on when query ID changes
  useEffect(() => {
    if (queryID) {
      trackProductImpressions(sHits, hitsPerPage, page, listName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryID]);

  // Do some Shopify Theme init after product list renders
  useEffect(() => {
    if (!(queryID && sliderRef.current && hits.length > 0)) return;
    window.theme.initCollectionAddToCart();
    if (isCarousel) {
      window.theme.initSlider(sliderRef.current);
    }
  }, [queryID, hits.length, isMobile, isCarousel]);

  // Render Loading
  if (isSearchStalled && !nbHits) {
    return <Loading isLoading mobileHeight="120vw" height="395px" />;
  }

  return (
    <div className="is-relative">
      <div
        ref={sliderRef}
        className={classnames(
          'columns product-list is-mobile',
          { 'scrollable zoomed-carousel no-slider-init': isCarousel, 'is-multiline is-variable is-2-mobile': !isCarousel })}
      >
        {hits.map((hit) => (
          <div key={hit.objectID} className={classnames('column product-tile-container', { 'is-half': !isCarousel, [styles.col]: !isCarousel })}>
            <ProductHit
              listName={listName}
              indexName={indexName}
              hit={hit}
              canClickThrough
              showSash
              showSingleImage
              isMobile={isMobile}
              addedToWishList={wishListProductIds?.includes(`${hit.id}`)}
              wishlistHasLoaded={Boolean(wishListProductIds)}
              wishListCollections={wishListCollections}
              showAddToCartAtBottom={false}
              showQuickView={false}
            />
          </div>
        ))}
      </div>
    </div>
  );
}));

ProductHitList.propTypes = {
  isMobile: PropTypes.bool.isRequired,
  listName: PropTypes.string.isRequired,
  searchResults: PropTypes.object,
  isSearchStalled: PropTypes.bool,
  hits: PropTypes.array,
  enableCarousel: PropTypes.bool.isRequired
};

ProductHitList.defaultProps = {
  hits: [],
  searchResults: null,
  isSearchStalled: true
};

export { CollectionListContainer };
